/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import * as React from 'react';
import { useCallback } from 'react';
import { Form, FormProps, FormRenderProps } from 'react-final-form';
import { setIn, FormSubscription, Mutator } from 'final-form';
import * as Yup from 'yup';
import asyncDebounce from '../../utilities/asyncDebounce';
import { IValidationError } from '@ngt/online-patient-management-ui';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
* ---------------------------------------------------------------------------------
* Implementation
* ---------------------------------------------------------------------------------
*/

type IFormValidationCreator<FormType> = (values: FormType) => Yup.Schema<{}>;

const formValidator = async <FormType extends {}>(
    validator: Yup.Schema<FormType>,
    validateValues: FormType
) => {
    try {
        await validator.validate(validateValues, { abortEarly: false, context: validateValues })

        return {};
    }
    catch (err: any) {
        const typedError: Yup.ValidationError = err;

        const errors = typedError.inner.reduce((formError, innerError) => {
            if (!formError[innerError.path]) {
                return setIn(formError, innerError.path, innerError.message);
            }

            return {};
        }, {});
        return errors;
    }
}

const formValidatorCreator = async <FormType extends {}>(
    validationCreator: IFormValidationCreator<FormType>,
    validateValues: FormType
) => {
    const validator = validationCreator(validateValues);

    return await formValidator(validator, validateValues);
};

export const DEFAULT_FORM_SUBSCRIPTION: FormSubscription = {
};

interface IAsyncFormProps<FormType = any> {
    component: (props: FormRenderProps<FormType>) => React.ReactNode;

    /**
     * The initial form data to load into the fields.
     */
    initialValues?: FormType;

    /**
     * Configuration for when the underlying final-form should rerender.
     */
    formSubscription?: FormProps<FormType>['subscription'];

    onValidate?: (values: FormType) => IValidationError[] | Promise<IValidationError[]>;
    validate?: Yup.Schema<FormType>;
    validateFn?: IFormValidationCreator<FormType>;
    mutators?: { [key: string]: Mutator };
    validationDebounceTimeout?: number | null;
    onSubmit?: (form: FormType) => void;
}

export const AsyncForm: React.FunctionComponent<IAsyncFormProps> = ({
    component,
    formSubscription,
    initialValues,
    onValidate,
    validate,
    validateFn,
    onSubmit,
    mutators,
    validationDebounceTimeout
}) => {

    const validateForm = useCallback(async (value: any) => {
        if (onValidate) {
            return onValidate(value);
        }

        if (validateFn) {
            return await formValidatorCreator(validateFn, value);
        }

        if (validate) {
            return await formValidator(validate, value);
        }

        return undefined;
    }, [validate, validateFn, onValidate]);

    const validator = React.useMemo(() => {
        return validationDebounceTimeout ?
            asyncDebounce(
                validateForm, validationDebounceTimeout) :
            validateForm;
    }, [validationDebounceTimeout, validateForm]);

    if (!onSubmit) {
        return null;
    }

    return (
        <Form
            initialValues={initialValues}
            onSubmit={onSubmit}
            subscription={formSubscription}
            validate={validator}
            mutators={mutators}
            render={component}
            keepDirtyOnReinitialize
        />
    );
};

export default AsyncForm;